import { memo } from "react";
import { Contact } from "services/api/contacts/types";
import Tooltip from "ui/components/Tooltip/Tooltip";

export interface ContactCellProps {
  contact: Contact | null | undefined;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const ContactCell: React.FC<ContactCellProps> = ({ contact, onClick }) => {
  if (!contact) return <></>;

  const isCompany = contact.contactType?.id === "C";
  const pictureUrl = contact.pictures?.length > 0 ? contact.pictures[0].url : null;
  const displayName = contact.name ?? (isCompany ? contact.companyName : `${contact.firstName} ${contact.lastName}`.trim()) ?? "-";

  const address = contact.primaryContactAddress;
  const fullAddress = address
    ? [address.line1, address.line2, `${address.postalCode ?? ""} ${address.city ?? ""}`.trim(), address.country].filter(Boolean).join(", ")
    : null;

  return (
    <Tooltip
      decorate={
        <div
          className="inline-flex items-center text-sm font-medium text-sky-600 hover:text-sky-900 cursor-pointer truncate"
          onClick={onClick}
        >
          {displayName}
        </div>
      }
      position="bottom-left"
      size="medium"
    >
        <div className="min-w-[260px]">
          {pictureUrl && (
            <div className="mb-3 rounded overflow-hidden">
              <img src={pictureUrl} alt={displayName} className="w-full h-[80px] object-cover rounded" />
            </div>
          )}
          <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">
            {isCompany ? "Tvrtka" : "Kontakt"}
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-300">Naziv</span>
            <span className="font-medium text-right max-w-[160px] truncate">{displayName}</span>
          </div>
         
          {contact.personalIdentificationNumber && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">OIB</span>
              <span className="font-medium">{contact.personalIdentificationNumber}</span>
            </div>
          )}
          {contact.contactType && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Tip</span>
              <span className="font-medium">{contact.contactType.name}</span>
            </div>
          )}
          {contact.jobTitle && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Pozicija</span>
              <span className="font-medium text-right max-w-[160px] truncate">{contact.jobTitle}</span>
            </div>
          )}
          {contact.department && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Odjel</span>
              <span className="font-medium text-right max-w-[160px] truncate">{contact.department}</span>
            </div>
          )}

          {(contact.primaryContactEmail || contact.primaryContactPhone || fullAddress) && (
            <>
              <div className="border-t border-slate-600 my-2"></div>
              {contact.primaryContactEmail && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Email</span>
                  <span className="font-medium text-sky-400 text-right max-w-[160px] truncate">{contact.primaryContactEmail.emailAddress}</span>
                </div>
              )}
              {contact.primaryContactPhone && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Telefon</span>
                  <span className="font-medium">{contact.primaryContactPhone.phoneNumber}</span>
                </div>
              )}
              {fullAddress && (
                <div className="text-sm mb-1">
                  <span className="text-slate-300">Adresa</span>
                  <div className="font-medium text-xs leading-relaxed mt-0.5">{fullAddress}</div>
                </div>
              )}
            </>
          )}

          {contact.contactGroups?.length > 0 && (
            <>
              <div className="border-t border-slate-600 my-2"></div>
              <div className="flex flex-wrap gap-1">
                {contact.contactGroups.map((g) => (
                  <span key={g.id} className="text-xs bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded">{g.name}</span>
                ))}
              </div>
            </>
          )}
        </div>
      </Tooltip>
  );
};

export default memo(ContactCell);
